import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Sparkles, Lightbulb, Rocket, Factory, Eye, FileCheck2, FileX2, ExternalLink } from "lucide-react";
import type { PortfolioProject } from "@/hooks/usePortfolioProjects";
import { Checkbox } from "@/components/ui/checkbox";

function getClassification(score: number) {
  if (score >= 400) return "Quick Win";
  if (score >= 300) return "Strategic Play";
  if (score >= 200) return "Experiment";
  return "Back-log";
}

const classColors: Record<string, string> = {
  "Quick Win": "bg-success/20 text-success border-success/30",
  "Strategic Play": "bg-info/20 text-info border-info/30",
  Experiment: "bg-warning/20 text-warning border-warning/30",
  "Back-log": "bg-muted text-muted-foreground border-border",
};

const statusLabels: Record<string, string> = {
  ideation: "Ideación", in_progress: "En progreso", completed: "Completado", paused: "Pausado",
};
const statusIcons: Record<string, React.ReactNode> = {
  ideation: <Lightbulb className="w-3 h-3" />,
  in_progress: <Rocket className="w-3 h-3" />,
  completed: <Factory className="w-3 h-3" />,
};

interface Props {
  projects: PortfolioProject[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onNavigate: (id: string) => void;
}

export function ProjectCardGrid({ projects, selectedIds, onToggleSelect, onNavigate }: Props) {
  if (projects.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>No hay proyectos entregados aún</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {projects.map(p => {
        const cls = getClassification(p.totalScore);
        return (
          <Card key={p.id} className="glass-card cursor-pointer group hover:ring-2 hover:ring-primary/30 transition-all">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between">
                <a
                  href={`/ai-portfolio/${p.id}`}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                    e.preventDefault();
                    onNavigate(p.id);
                  }}
                  className="flex items-center gap-2 flex-1 min-w-0 no-underline text-inherit"
                >
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-semibold text-foreground text-sm truncate">{p.name}</p>
                      <Eye className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{p.company || "Sin empresa"}</p>
                  </div>
                </a>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`/ai-portfolio/${p.id}`, '_blank', 'noopener,noreferrer');
                    }}
                    className="text-muted-foreground hover:text-primary p-1"
                    title="Abrir en nueva pestaña"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                  <Checkbox
                    checked={selectedIds.has(p.id)}
                    onCheckedChange={() => onToggleSelect(p.id)}
                    className="mt-1"
                    onClick={e => e.stopPropagation()}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Progress value={(p.totalScore / 500) * 100} className="flex-1 h-2" />
                <span className="font-bold text-foreground text-sm">{p.totalScore}</span>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`${classColors[cls]} border text-xs`}>{cls}</Badge>
                <Badge variant="outline" className="text-xs gap-1">
                  {statusIcons[p.status]}{statusLabels[p.status] || p.status}
                </Badge>
                {p.cohortName && <Badge variant="secondary" className="text-xs">{p.cohortName}</Badge>}
                {p.hasDeliverable ? (
                  <Badge className="bg-success/20 text-success border-success/30 border text-xs gap-1">
                    <FileCheck2 className="w-3 h-3" />
                    Entregable{p.deliverableCount > 1 ? ` (${p.deliverableCount})` : ""}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-muted-foreground gap-1">
                    <FileX2 className="w-3 h-3" />
                    Sin entregable
                  </Badge>
                )}
              </div>

              {p.tools.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {p.tools.slice(0, 3).map(t => (
                    <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                  ))}
                  {p.tools.length > 3 && (
                    <Badge variant="outline" className="text-xs">+{p.tools.length - 3}</Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
